/**
 * The API surface the frontend reads, and the shapes the backend promises.
 *
 * Every request uses a relative URL on purpose: the frontend is served from
 * the same origin as the API (phase 4 doc, H-5), in development through Vite's
 * proxy and in production from FastAPI itself, so there is no base URL to
 * configure and nothing that could point at a different host.
 *
 * A bearer token, when the caller has one, travels in the `Authorization`
 * header. Nothing here acquires one; that is the session's job.
 */

export interface HealthOut {
  status: string;
  version: string;
  db_path: string;
  auth_mode: string;
  locations: number;
  cash_drop_submissions: number;
  safe_count_submissions: number;
  petty_cash_entries: number;
  petty_cash_seed_balance_confirmed: boolean;
}

export interface AuthConfigOut {
  auth_mode: string;
  authority: string | null;
  tenant_id: string | null;
  client_id: string | null;
  scopes: string[];
  allowed_upn_domain: string;
}

export interface SubmissionsOut {
  submissions: unknown[];
  count: number;
}

/** A response as the diagnostics screen wants to show it: status first, body second. */
export interface Observed<T> {
  status: number;
  ok: boolean;
  body: T | { detail?: string } | null;
}

function headersFor(token?: string): HeadersInit {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function getJson<T>(path: string, token?: string): Promise<T> {
  const response = await fetch(path, { headers: headersFor(token) });
  if (!response.ok) {
    throw new Error(`${path} returned ${response.status}`);
  }
  return (await response.json()) as T;
}

/** Like `getJson`, but a non-2xx is a result to display, not an exception. */
async function observe<T>(path: string, token?: string): Promise<Observed<T>> {
  const response = await fetch(path, { headers: headersFor(token) });
  let body: Observed<T>["body"] = null;
  try {
    body = (await response.json()) as T;
  } catch {
    body = null;
  }
  return { status: response.status, ok: response.ok, body };
}

export const fetchHealth = (token?: string) => getJson<HealthOut>("/health", token);
export const fetchAuthConfig = () => getJson<AuthConfigOut>("/auth/config");
export const observeSubmissions = (token?: string) =>
  observe<SubmissionsOut>("/cash-drop/submissions", token);

// --- Cash Drop: submit form + attachments + reconciliation (phase 6) ----------
//
// `till`/`tip`/`total` travel as strings, both ways: the backend stores them as
// Decimal text (`cash_drop/storage.py`, "Money is stored as TEXT holding the
// Decimal's string form... exact and reads back with Decimal(value)"), and a
// JS `number` would reintroduce the float rounding that design goes out of its
// way to avoid. Nothing in this file adds, subtracts, or otherwise computes a
// money value - see `package.json`'s own description: "Renders what the
// backend computes; does no money arithmetic."

/** Mirrors `config.locations.Location` exactly - the six values the backend enum accepts. */
export const LOCATIONS = [
  "Kennedy",
  "St Pete",
  "MB - Ybor Wholesale",
  "MB - Ybor Retail",
  "MB - FL Ave Wholesale",
  "MB - FL Ave Retail",
] as const;
export type Location = (typeof LOCATIONS)[number];

/** Mirrors `cash_drop/router.py`'s `CashDropSubmissionIn`. No `submitted_by` -
 * the backend binds it to the signed-in principal and ignores a client value. */
export interface CashDropSubmissionIn {
  location: Location;
  drop_date: string; // ISO date, yyyy-mm-dd
  till: string;
  tip: string;
  bank_deposit_amount: string; // exact amount in the envelope, as entered
  total: string;
  attachment_ids: string[];
}

/** Mirrors `cash_drop/storage.py`'s `CashDropRecord` - what a successful submit returns. */
export interface CashDropRecord {
  id: string;
  submitted_at: string;
  submitted_by: string;
  location: Location;
  drop_date: string;
  till: string;
  tip: string;
  total: string;
  expected_deposit: string;
  undeposited_balance: string;
  bank_deposit_amount: string;
  attachment_ids: string[];
  deposit_source: "entered" | "computed";
  bc_company: string;
  bustyp: string | null;
}

/** Mirrors `attachments/models.py`'s `AttachmentOut` - what a successful upload returns. */
export interface AttachmentOut {
  attachment_id: string;
  original_filename: string;
  content_type: string;
  byte_size: number;
}

/** Mirrors `cash_drop/reconciliation.py`'s `ReconciliationRow` / `SharedDepositTotal` / `ReconciliationOut`. */
export interface ReconciliationRow {
  drop_date: string;
  location: Location;
  bc_company: string;
  bustyp: string | null;
  shared_bank_account_group: string | null;
  submissions: number;
  till: string;
  tip: string;
  bank_deposit_amount: string;
  undeposited_balance: string;
  submitted_by: string[];
  deposit_sources: string[];
}

export interface SharedDepositTotal {
  drop_date: string;
  shared_bank_account_group: string;
  locations: Location[];
  bank_deposit_amount: string;
}

export interface ReconciliationOut {
  date_from: string | null;
  date_to: string | null;
  rows: ReconciliationRow[];
  shared_deposits: SharedDepositTotal[];
}

/** The shape of a FastAPI `detail`: a plain string from a route's own
 * `HTTPException` (e.g. `_validate_attachment_ids`, always a string naming the
 * offending id), or an array of Pydantic validation errors from automatic
 * request-body validation (an unknown `location`, more than 3 attachment ids).
 * Never invented wording of our own - both shapes are rendered from what the
 * backend actually said. */
export type ErrorDetail = string | { msg: string; loc?: unknown[] }[] | undefined;

export function formatDetail(detail: ErrorDetail): string {
  if (detail === undefined) return "";
  if (typeof detail === "string") return detail;
  return detail
    .map((item) => {
      const loc = Array.isArray(item.loc) ? item.loc.slice(1).join(".") : "";
      return loc ? `${loc}: ${item.msg}` : item.msg;
    })
    .join("; ");
}

/** Thrown by `submitCashDrop` / `uploadAttachment` on a non-2xx. Carries the
 * status and the raw `detail` so a caller can inspect either; `.message` is
 * already `formatDetail(detail)` for the common case of just displaying it. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: ErrorDetail;

  constructor(status: number, detail: ErrorDetail) {
    super(formatDetail(detail) || `request failed (${status})`);
    this.status = status;
    this.detail = detail;
  }
}

async function parseBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function detailOf(body: unknown): ErrorDetail {
  if (body && typeof body === "object" && "detail" in body) {
    return (body as { detail: ErrorDetail }).detail;
  }
  return undefined;
}

async function postJson<T>(path: string, body: unknown, token?: string): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    headers: { ...headersFor(token), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const parsed = await parseBody(response);
  if (!response.ok) throw new ApiError(response.status, detailOf(parsed));
  return parsed as T;
}

async function postForm<T>(path: string, form: FormData, token?: string): Promise<T> {
  // No Content-Type header: the browser sets `multipart/form-data` with the
  // boundary itself. Setting it here would drop the boundary and the backend
  // would fail to parse the body at all.
  const response = await fetch(path, { method: "POST", headers: headersFor(token), body: form });
  const parsed = await parseBody(response);
  if (!response.ok) throw new ApiError(response.status, detailOf(parsed));
  return parsed as T;
}

/** `POST /cash-drop/submissions`. Throws `ApiError` on any non-2xx, including
 * the phase 5 attachment-linking checks (unknown id, wrong uploader, already
 * claimed) and ordinary field validation (bad location, negative amount). */
export const submitCashDrop = (body: CashDropSubmissionIn, token?: string) =>
  postJson<CashDropRecord>("/cash-drop/submissions", body, token);

/** `POST /cash-drop/attachments`, one file per call, matching the backend's
 * "one file per request" design (phase 5 doc). Throws `ApiError` - 413 too
 * large, 415 wrong type (sniffed from bytes, never this call's declared type) -
 * for the caller to show against the specific slot that failed. */
export const uploadAttachment = (file: File, token?: string) =>
  uploadFileTo("/cash-drop/attachments", file, token);

function uploadFileTo(path: string, file: File, token?: string): Promise<AttachmentOut> {
  const form = new FormData();
  form.append("file", file);
  return postForm<AttachmentOut>(path, form, token);
}

export interface ReconciliationFilter {
  dateFrom?: string;
  dateTo?: string;
  location?: Location;
}

/** `GET /cash-drop/reconciliation`. Uses `observe`, not `postJson`'s throwing
 * shape: a 403 here is an expected response for most accounts (phase 6 doc,
 * "Roles"), not a failure to catch - the caller renders whatever comes back. */
export function fetchReconciliation(
  filter: ReconciliationFilter,
  token?: string,
): Promise<Observed<ReconciliationOut>> {
  return observe<ReconciliationOut>(`/cash-drop/reconciliation${reconciliationQuery(filter)}`, token);
}

function reconciliationQuery(filter: ReconciliationFilter): string {
  const params = new URLSearchParams();
  if (filter.dateFrom) params.set("date_from", filter.dateFrom);
  if (filter.dateTo) params.set("date_to", filter.dateTo);
  if (filter.location) params.set("location", filter.location);
  const query = params.toString();
  return query ? `?${query}` : "";
}

// --- Safe Count: submit form + attachments + reconciliation (safe-count.md) ---
//
// Same money rule as Cash Drop: every amount is a string, both ways. There is
// no `total` in the request at all - the backend computes it (safe-count.md,
// Decision 1) - and the form never adds the fields up, not even for display.

/** The five field names that occur across the three per-location field sets
 * (`safe_count/models.py`), with the labels ClickUp used for them. */
export const SAFE_COUNT_FIELD_LABELS = {
  drawers: "Drawers",
  coins: "Coins / Change",
  petty: "Petty",
  register: "Register",
  bills: "Bills",
} as const;
export type SafeCountFieldKey = keyof typeof SAFE_COUNT_FIELD_LABELS;

/** Which fields each location counts, in the order ClickUp showed them
 * (safe-count.md, section 1). Mirrors `LOCATION_FIELD_SCHEMA`: Kennedy shares
 * Ybor's schema but lists Petty first. The backend validates the *set* and
 * ignores order, so the order here is purely what the manager sees. */
export const SAFE_COUNT_FIELDS: Record<Location, readonly SafeCountFieldKey[]> = {
  "MB - Ybor Retail": ["drawers", "coins", "petty"],
  "MB - Ybor Wholesale": ["drawers", "coins", "petty"],
  Kennedy: ["petty", "coins", "drawers"],
  "MB - FL Ave Retail": ["petty", "register", "coins"],
  "MB - FL Ave Wholesale": ["petty", "register", "coins"],
  "St Pete": ["bills", "coins", "drawers"],
};

/** A location's field set on the wire: exactly that location's keys, each a
 * Decimal string. Partial because which keys are present depends on
 * `location`; a wrong-shape set is a 422 from the backend naming what that
 * location expects, never something this client corrects. */
export type SafeCountFields = Partial<Record<SafeCountFieldKey, string>>;

/** Mirrors `safe_count/models.py`'s `SafeCountSubmissionIn`. No `submitted_by`
 * and no `total` - both are ignored by the backend if sent. */
export interface SafeCountSubmissionIn {
  location: Location;
  safe_count_as_of: string; // ISO date, yyyy-mm-dd
  fields: SafeCountFields;
  attachment_ids: string[]; // 0-3; optional, unlike Cash Drop
}

/** Mirrors `safe_count/storage.py`'s `SafeCountRecord`. No BC tags (Decision 2). */
export interface SafeCountRecord {
  id: string;
  submitted_at: string;
  submitted_by: string;
  location: Location;
  safe_count_as_of: string;
  fields: SafeCountFields;
  total: string;
  attachment_ids: string[];
}

/** Mirrors `safe_count/reconciliation.py`'s `ReconciliationOut`: the filtered
 * records themselves with their totals - a read surface, not a variance
 * table (safe-count.md, section 3). */
export interface SafeCountReconciliationOut {
  date_from: string | null;
  date_to: string | null;
  location: Location | null;
  count: number;
  rows: SafeCountRecord[];
}

/** `POST /safe-count/submissions`. Throws `ApiError` on any non-2xx, including
 * a field set that does not match the location (422 naming the expected set). */
export const submitSafeCount = (body: SafeCountSubmissionIn, token?: string) =>
  postJson<SafeCountRecord>("/safe-count/submissions", body, token);

/** `POST /safe-count/attachments`: the same one-file-per-request upload as
 * Cash Drop's, on Safe Count's own prefix. Same blob store behind both. */
export const uploadSafeCountAttachment = (file: File, token?: string) =>
  uploadFileTo("/safe-count/attachments", file, token);

/** `GET /safe-count/reconciliation`. `observe`d like Cash Drop's: a 403 is an
 * expected answer for most accounts and is rendered, not thrown. */
export function fetchSafeCountReconciliation(
  filter: ReconciliationFilter,
  token?: string,
): Promise<Observed<SafeCountReconciliationOut>> {
  return observe<SafeCountReconciliationOut>(
    `/safe-count/reconciliation${reconciliationQuery(filter)}`,
    token,
  );
}

// --- Petty Cash: submit form + attachments + reconciliation (petty-cash-consolidation.md) --
//
// Same money-as-string rule as the other two forms. There is no `submitted_by`
// in the request - the backend binds it to the signed-in principal - and no
// `signed_amount` / `expected_balance` / `balance_discrepancy` /
// `discrepancy_amount` either: those are server-computed from the running
// ledger (Rulings 1 and 2) and only ever appear on what the backend returns.

export const ENTRY_TYPES = ["expense", "replenishment"] as const;
export type EntryType = (typeof ENTRY_TYPES)[number];

export const ENTRY_TYPE_LABELS: Record<EntryType, string> = {
  expense: "Expense (decreases the fund)",
  replenishment: "Replenishment (increases the fund)",
};

/** Mirrors `petty_cash/models.py`'s `PettyCashEntryIn`. No `submitted_by`. */
export interface PettyCashEntryIn {
  transaction_date: string; // ISO date, yyyy-mm-dd
  purpose: string;
  entry_type: EntryType;
  amount: string;
  reported_balance: string;
  attachment_ids: string[]; // 1-2, required
}

/** Mirrors `petty_cash/storage.py`'s `PettyCashRecord` - what a successful
 * submit (and the ledger/reconciliation views) return. */
export interface PettyCashRecord {
  id: string;
  submitted_at: string;
  submitted_by: string;
  transaction_date: string;
  purpose: string;
  entry_type: EntryType;
  amount: string;
  signed_amount: string;
  reported_balance: string;
  expected_balance: string;
  balance_discrepancy: boolean;
  discrepancy_amount: string;
  attachment_ids: string[];
  /** Soft void (never deleted): a voided entry stays in the ledger history but no
   * longer moves the running balance. */
  voided: boolean;
  voided_at: string | null;
  voided_by: string | null;
  void_reason: string | null;
}

/** Mirrors `petty_cash/router.py`'s `PettyCashListOut`. */
export interface PettyCashListOut {
  submissions: PettyCashRecord[];
  count: number;
}

/** Mirrors `petty_cash/reconciliation.py`'s `ReconciliationOut`: the ledger,
 * filtered by date range, each entry carrying its own discrepancy flag - a
 * read surface, not a variance engine, exactly like Safe Count's. */
export interface PettyCashReconciliationOut {
  date_from: string | null;
  date_to: string | null;
  count: number;
  rows: PettyCashRecord[];
}

/** `POST /petty-cash/submissions`. Throws `ApiError` on any non-2xx. */
export const submitPettyCash = (body: PettyCashEntryIn, token?: string) =>
  postJson<PettyCashRecord>("/petty-cash/submissions", body, token);

/** `POST /petty-cash/submissions/{id}/void`. Throws `ApiError` on any non-2xx
 * (403 for a non-`reconciliation_viewer`, 409 for an already-voided entry) so
 * the caller can show the server's own message. */
export const voidPettyCash = (id: string, reason: string, token?: string) =>
  postJson<PettyCashRecord>(`/petty-cash/submissions/${encodeURIComponent(id)}/void`, { reason }, token);

/** `POST /petty-cash/attachments`: the same one-file-per-request upload as
 * the other two forms', on Petty Cash's own prefix. */
export const uploadPettyCashAttachment = (file: File, token?: string) =>
  uploadFileTo("/petty-cash/attachments", file, token);

/** `GET /petty-cash/submissions`. Unlike the two reconciliation-gated views
 * below, this is readable by any authenticated staff account (docs/phases/
 * petty-cash-consolidation.md, Part 3): Massiel and Kevin each need the
 * current running balance to fill in `reported_balance` on their next entry. */
export function fetchPettyCashLedger(
  filter: Pick<ReconciliationFilter, "dateFrom" | "dateTo">,
  token?: string,
): Promise<Observed<PettyCashListOut>> {
  return observe<PettyCashListOut>(`/petty-cash/submissions${reconciliationQuery(filter)}`, token);
}

/** `GET /petty-cash/reconciliation`. `observe`d like the other two forms': a
 * 403 is an expected answer for most accounts and is rendered, not thrown. */
export function fetchPettyCashReconciliation(
  filter: Pick<ReconciliationFilter, "dateFrom" | "dateTo">,
  token?: string,
): Promise<Observed<PettyCashReconciliationOut>> {
  return observe<PettyCashReconciliationOut>(
    `/petty-cash/reconciliation${reconciliationQuery(filter)}`,
    token,
  );
}
